import React, { useCallback, useMemo, useState } from 'react'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import NoteAddCN from 'components/NoteAddCN'
import NoteSearchEmpty from './NoteSearchEmpty'
import { Sheet } from 'components/ui/sheet'
import { cn } from 'lib/utils'
import { filterNotesBySearch } from 'utils/noteSearch'
import useNoteFacets, { applyNoteFilters, groupNotesByDate } from 'hooks/useNoteFacets'
import type { LibraryImage } from 'utils/libraryImages'
import NotesWorkspaceHeader from './NotesWorkspaceHeader'
import NotesRail from './NotesRail'
import NotesEmptyState from './NotesEmptyState'
import NoteThread from './NoteThread'
import DeleteNoteDialog from './DeleteNoteDialog'
import { NoteTasksDialog } from 'features/AskMdt/components/NoteTasksDialog'
import { extractNoteImageUrls } from 'utils/noteImages'
import type {
	NoteFilterId,
	NoteImageEntry,
	NotesSurfaceAdapter,
	WorkspaceNote,
} from './notesWorkspace.types'

interface NotesWorkspaceProps {
	adapter: NotesSurfaceAdapter
	className?: string
}

/** Which note the rail asked to open, and at which of its images. */
interface ImageTarget {
	noteId: string
	index: number
}

/**
 * The two-column Notes layout.
 *
 * A wide feed with a rail that reports on the note set itself: who is writing,
 * what is filtered, and every image at once. All of the rail's data is derived
 * client-side, because the note endpoints take only an entity id and every
 * surface already holds its whole set in Redux.
 *
 * The rail is a real grid column at xl and above. Below that the feed needs the
 * full width, so the same rail moves into a Sheet behind the header's Filters
 * button rather than being cut down or stacked out of reach.
 */
const NotesWorkspace: React.FC<NotesWorkspaceProps> = ({ adapter, className }) => {
	const [search, setSearch] = useState('')
	const [filter, setFilter] = useState<NoteFilterId>('all')
	const [authorId, setAuthorId] = useState<string | null>(null)
	const [filtersOpen, setFiltersOpen] = useState(false)
	const [composerOpen, setComposerOpen] = useState(false)
	const [parentId, setParentId] = useState<string | null>(null)
	const [imageTarget, setImageTarget] = useState<ImageTarget | null>(null)
	const [deleting, setDeleting] = useState<WorkspaceNote | null>(null)
	const [deleteBusy, setDeleteBusy] = useState(false)
	const [deleteError, setDeleteError] = useState<string | null>(null)
	// The note "Make tasks" was asked of (MYD-643).
	const [tasksFor, setTasksFor] = useState<string | null>(null)

	const facets = useNoteFacets(adapter.notes)

	/**
	 * Whether to offer delete on one note.
	 *
	 * The rule lives here and only here. A surface answers the "who" half
	 * through `adapter.canDeleteNote`; the "when" half — that a discussed note is
	 * deletable by nobody, including its author — is added here so that no
	 * surface has to remember it and none can disagree about it.
	 *
	 * This mirrors `noteDeleteGuards.canDeleteNote` on the server, which is the
	 * thing that actually enforces it. Matching it means the control is not
	 * offered for an action that would come back 403 or 409; it does not mean the
	 * frontend is trusted.
	 *
	 * The reply count comes from `facets.repliesByParent`, which is built from
	 * the same array the server counted — the note endpoints return replies in
	 * the set, so this needs no extra request.
	 */
	const canDeleteNote = useCallback(
		(note: WorkspaceNote) => {
			if (!adapter.deleteNote || !adapter.canDeleteNote?.(note)) return false
			return (facets.repliesByParent[note._id] || []).length === 0
		},
		[adapter, facets.repliesByParent],
	)

	const confirmDelete = useCallback(async () => {
		if (!deleting || !adapter.deleteNote) return
		setDeleteBusy(true)
		setDeleteError(null)

		// The surface resolves to the server's refusal rather than throwing, so a
		// 409 lands in the dialog instead of an unhandled rejection.
		const refusal = await adapter.deleteNote(deleting)
		setDeleteBusy(false)

		if (refusal) {
			setDeleteError(refusal)
			return
		}
		setDeleting(null)
	}, [adapter, deleting])

	const closeDelete = useCallback(() => {
		setDeleting(null)
		setDeleteError(null)
	}, [])

	// Filters and search compose: the quick filter narrows the set, then the
	// same matcher the rail's gallery uses runs over what is left, so the feed
	// and the rail never disagree about what a search term returns.
	const visibleNotes = useMemo<WorkspaceNote[]>(() => {
		const filtered = applyNoteFilters(
			facets.topLevel,
			{ filter, authorId },
			{ currentUserId: adapter.currentUserId, repliesByParent: facets.repliesByParent },
		)
		return filterNotesBySearch(filtered, search, adapter.imageRecords)
	}, [facets, filter, authorId, search, adapter.currentUserId, adapter.imageRecords])

	// The gallery tracks the feed rather than being a second, independent view.
	const visibleImages = useMemo<NoteImageEntry[]>(() => {
		const ids = new Set(visibleNotes.map(note => note._id))
		return facets.images.filter(entry => ids.has(entry.noteId))
	}, [facets.images, visibleNotes])

	const dateGroups = useMemo(() => groupNotesByDate(visibleNotes), [visibleNotes])

	const hasActiveFilters = filter !== 'all' || Boolean(authorId)
	const isSearching = search.trim().length > 0
	const hasAnyNotes = facets.topLevel.length > 0

	const openComposer = (parent: string | null) => {
		setParentId(parent)
		setComposerOpen(true)
	}

	const handleSubmit = async (note: {
		content: string
		contents?: string[]
		parentid: string | null
		libraryImages?: LibraryImage[]
	}) => {
		setComposerOpen(false)
		// `contents` carries every body the composer built; `content` is only its
		// first entry, kept for callers that predate the split. Fall back to it so
		// an older composer still creates a note rather than nothing.
		const contents = note.contents?.length ? note.contents : [note.content]
		await adapter.createNote({ ...note, contents })
	}

	/**
	 * A rail thumbnail opens the image where it actually lives. Scrolling to the
	 * note first keeps the click legible: the dialog opens over the note that
	 * owns the picture, not over an unrelated part of the feed.
	 */
	const handleRailImageSelect = (entry: NoteImageEntry) => {
		setFiltersOpen(false)
		setImageTarget({ noteId: entry.noteId, index: entry.indexInNote })
		document
			.getElementById(`note-${entry.noteId}`)
			?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	const rail = (
		<NotesRail
			facets={facets}
			visibleImages={visibleImages}
			filter={filter}
			onFilterChange={next => {
				setFilter(next)
				setFiltersOpen(false)
			}}
			activeAuthorId={authorId}
			onAuthorChange={setAuthorId}
			onImageSelect={handleRailImageSelect}
			supportsReplies={adapter.supportsReplies}
		/>
	)

	if (adapter.isLoading && adapter.notes.length === 0) {
		return <Spinner containerHeight="40vh" />
	}

	let feed: React.ReactNode

	if (!hasAnyNotes) {
		feed = <NotesEmptyState scopeLabel={adapter.subtitle} onCreateNote={() => openComposer(null)} />
	} else if (visibleNotes.length === 0) {
		feed = isSearching ? (
			<NoteSearchEmpty query={search.trim()} onClear={() => setSearch('')} />
		) : (
			<div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center">
				<p className="m-0 text-sm text-muted-foreground">No notes match the current filter.</p>
				<button
					type="button"
					onClick={() => {
						setFilter('all')
						setAuthorId(null)
					}}
					className="mt-2 text-sm font-medium text-primary hover:underline"
				>
					Clear filters
				</button>
			</div>
		)
	} else {
		feed = (
			<div className="space-y-8">
				{dateGroups.map(group => (
					<section key={group.id}>
						<h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							{group.label}
						</h3>
						<div className="space-y-4">
							{group.notes.map(note => (
								<NoteThread
									key={note._id}
									note={note}
									replies={facets.repliesByParent[note._id] || []}
									imageRecords={adapter.imageRecords}
									currentUserId={adapter.currentUserId}
									canReply={adapter.supportsReplies}
									tag={adapter.tagForNote?.(note) ?? null}
									onReply={openComposer}
									onUpdateImage={adapter.updateImage}
									onRemoveImage={adapter.removeImage}
									canDeleteNote={canDeleteNote}
									onDeleteNote={adapter.deleteNote ? setDeleting : undefined}
									onMakeTasks={adapter.canMakeTasks ? target => setTasksFor(target._id) : undefined}
									openImageIndex={
										imageTarget?.noteId === note._id ? imageTarget.index : null
									}
									onImageReviewClose={() => setImageTarget(null)}
								/>
							))}
						</div>
					</section>
				))}
			</div>
		)
	}

	return (
		// No overflow class on this root: `main` is the scroll container, and any
		// overflow here would silently kill the rail's sticky positioning.
		<div className={cn('space-y-6', className)} data-testid={`notes-workspace-${adapter.key}`}>
			<NotesWorkspaceHeader
				title={adapter.title}
				subtitle={adapter.subtitle}
				visibleCount={visibleNotes.length}
				totalCount={facets.topLevel.length}
				search={search}
				onSearchChange={setSearch}
				onNewNote={() => openComposer(null)}
				onOpenFilters={() => setFiltersOpen(true)}
				hasActiveFilters={hasActiveFilters}
				showFilters={hasAnyNotes}
			/>

			{adapter.error && <Message variant="danger">{adapter.error}</Message>}

			<div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_260px]">
				{/* Notes are prose. Past roughly 640px a line of text is tiring to
				    read and the cards start to sprawl, so the column caps there and
				    centres rather than stretching to fill a wide screen. */}
				<div className="mx-auto w-full min-w-0 max-w-[640px]" data-testid="notes-feed">
					{feed}
				</div>

				{/* Empty facets read as a broken panel, so the rail waits for content. */}
				{hasAnyNotes && (
					<div className="hidden xl:sticky xl:top-0 xl:block xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto">
						{rail}
					</div>
				)}
			</div>

			<Sheet
				open={filtersOpen && hasAnyNotes}
				onClose={() => setFiltersOpen(false)}
				side="right"
				className="w-[340px] max-w-[90vw] overflow-y-auto bg-gray-50 p-4"
			>
				<h2 className="mb-4 mt-0 text-lg font-semibold text-foreground">Filters</h2>
				{rail}
			</Sheet>

			<NoteAddCN
				open={composerOpen}
				onClose={() => setComposerOpen(false)}
				onSubmit={handleSubmit}
				parentid={parentId}
			/>

			{/*
			 * One dialog for the whole feed rather than one per card. The same
			 * component the field app uses, so the wording, the photograph count and
			 * the Cancel-first layout cannot drift between the two surfaces.
			 */}
			<DeleteNoteDialog
				open={Boolean(deleting)}
				onClose={closeDelete}
				onConfirm={confirmDelete}
				imageCount={deleting ? extractNoteImageUrls(deleting.note).length : 0}
				busy={deleteBusy}
				error={deleteError}
			/>

			{/* Keyed by note, so each note asks afresh. */}
			{tasksFor && <NoteTasksDialog key={tasksFor} open noteId={tasksFor} onClose={() => setTasksFor(null)} />}
		</div>
	)
}

export default NotesWorkspace
